
import Vue, { PropType, computed, ComputedRef } from 'vue';
import { w2pPintura } from './components/W2PPintura';
import { ActionsToolbar } from './components/ActionsToolbar';
import { WizardHeader } from './components/Header';
import {
  TextPanelOptions,
  ImagePanelOptions,
  HomePanel,
  LanyardPanelOptions,
} from './components/Panels';
import { OrderDetails } from './components/OrderDetails';
import { PositionsManagement } from './components/PositionsManagement';
import { PreviewSection } from './components/PreviewSection';
import PreviewPdf from './components/PreviewSection/components/PreviewPdf.vue';
import {
  ConstructPersonalizationFormWrapper,
  CreatePersonalizationFormWrapper,
} from '../import-wizard/typings/common/createPersonalization';

import {
  PANELS_WIDTH,
  ICONS_TOOLBAR_WIDTH,
  POSITIONS_MANAGER_WIDTH,
  HEADER_HEIGHT,
  PINTURA_MAX_MARGIN_LEFT,
  PINTURA_MAX_MARGIN_RIGHT,
  ORDER_DETAILS_WIDTH,
  ORDER_DETAILS_HEIGHT,
  PANELS_HEIGHT_PADDING,
  RIGHT_SECTIONS_HEIGHT_PADDING,
  HOME_PANEL,
  TEXT_PANEL,
  IMAGE_PANEL,
  LANYARDS_PANEL,
} from './constants';
import { ProductPrintPositionAddModel } from '@/typings';
import {
  PdpChoicesSetterType,
  PriceDetailsSetterType,
} from '@/setters/typings';
import { LogoOnlyArtwork, WizardTourStep } from '../import-wizard/typings';
import { SupportedLangCodes } from '@/constants/languageCodes';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { pdpChoices } from '@/setters';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import {
  mapPositionsFromPdp,
  mapProductPrintPositions,
  mapSelectedVariant,
} from '@/utils/productMapper';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { mapActions, mapState, mapStores } from 'pinia';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { AnnotationShape } from './typings/common';
import LogoLibrary from '../logo-library/TheApp.vue';
import LogoUpload from '../logo-upload/TheApp.vue';
import { ArtworkPayload } from '../import-wizard/typings/pintura';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import CropEditor from '../import-wizard-v2/components/CropEditor/CropEditor.vue';
import { NotificationsManagement } from './components/NotificationsManagement';
import {
  Personalization,
  ProductPrintData,
  CreatePersonalizationForm,
} from '@midocean/webapps-shared-domain';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { CvLoading } from '@carbon/vue';
import ManualCheck from './components/SmallDetails/ManualCheck/ManualCheck.vue';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import TourModal from './components/WizardTour/TourModal.vue';
import { base64StringToBlob } from 'blob-util';
import { Ruler } from './components/Ruler';
import { EmptyPrintPosition } from './components/EmptyPrintPosition';
import VueTour from '@/components/VueTour/main';
import { tourStepsMapper } from '@/utils/tourStepsMapper';
import customersAPI from '@/services/CustomersService';
import { CustomPinturaToolbar } from './components/CustomPinturaToolbar';
import { isAlignButtonDisabled } from '@/utils/alignmentHelper';
import { ProductColorSetterType } from '@/setters/typings/common/ProductColorsSetterType';
import {
  getArtworkFiles,
  getProofFormFromState,
  extractCommercialRemarks,
} from '@/utils/personalizationHelperV2';

Vue.use(VueTour);

export default Vue.extend({
  components: {
    WizardHeader,
    ActionsToolbar,
    PositionsManagement,
    PreviewSection,
    w2pPintura,
    TextPanelOptions,
    HomePanel,
    ImagePanelOptions,
    LanyardPanelOptions,
    LogoLibrary,
    LogoUpload,
    CropEditor,
    OrderDetails,
    CvLoading,
    PreviewPdf,
    NotificationsManagement,
    ManualCheck,
    TourModal,
    Ruler,
    EmptyPrintPosition,
    CustomPinturaToolbar,
  },
  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'none',
      validator: isValidLanguageValidator,
    },
    context: {
      type: String,
      required: false,
      default: '',
    },
    personalizationJson: {
      type: Object as PropType<CreatePersonalizationFormWrapper>,
      required: false,
      default: () => {
        return {} as CreatePersonalizationFormWrapper;
      },
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    productDescription: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    customTitle: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    priceDetails: {
      type: Object as PropType<PriceDetailsSetterType>,
      required: true,
    },
    pdpChoices,
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFirstTimeOpened: {
      type: Boolean,
      required: false,
      default: true,
    },
    isCommunicationSentToCC: {
      type: Boolean,
      required: false,
      default: false,
    },
    notifyCustomerOnFirstProof: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConceptDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOrderDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    printPositions: {
      type: [] as PropType<ProductPrintPositionAddModel[]>,
      required: false,
      default() {
        return [];
      },
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    logoOnlyArtwork: {
      type: [] as PropType<LogoOnlyArtwork[]>,
      required: false,
      default: () => {
        return [];
      },
    },
    version: {
      type: String,
      required: false,
      default: 'V2',
    },
    sapCustomerEmail: {
      type: String,
      required: false,
      default: '',
    },
    customerCareEmail: {
      type: String,
      required: false,
      default: '',
    },
    region: {
      type: String,
      required: false,
      default: '',
    },
    userLoginOptions: {
      type: Object as PropType<{ data: string; iv: string }>,
      required: false,
      default: () => {
        return { data: '', iv: '' };
      },
    },
  },

  provide(): {
    $pdpChoices: ComputedRef<PdpChoicesSetterType>;
    $sapCustomerId: ComputedRef<string>;
    $isEditMode: ComputedRef<boolean>;
    $personalization: ComputedRef<Personalization>;
    $isDirectApproval: ComputedRef<boolean>;
    $isConceptDisabled: ComputedRef<boolean>;
    $isOrderDisabled: ComputedRef<boolean>;
    $customerCompanyLogoUrl: ComputedRef<string>;
    $context: ComputedRef<string>;
    $userLoginOptions: ComputedRef<{ data: string; iv: string }>;
    $isCommunicationSentToCC: ComputedRef<boolean>;
    $notifyCustomerOnFirstProof: ComputedRef<boolean>;
    $customerCareEmail: ComputedRef<string>;
  } {
    return {
      $pdpChoices: computed(() => this.pdpChoices),
      $sapCustomerId: computed(() => this.sapCustomerId),
      $isEditMode: computed(() => this.isEditMode),
      $personalization: computed(() => this.personalization),
      $isDirectApproval: computed(() => this.isDirectApproval),
      $isOrderDisabled: computed(() => this.isOrderDisabled),
      $isConceptDisabled: computed(() => this.isConceptDisabled),
      $customerCompanyLogoUrl: computed(() => this.customerCompanyLogoUrl),
      $context: computed(() => this.context),
      $userLoginOptions: computed(() => this.userLoginOptions),
      $isCommunicationSentToCC: computed(() => this.isCommunicationSentToCC),
      $notifyCustomerOnFirstProof: computed(
        () => this.notifyCustomerOnFirstProof
      ),
      $customerCareEmail: computed(() => this.customerCareEmail),
    };
  },

  data() {
    return {
      DoSmallDetailsPart,
      rootElement: null as unknown as HTMLElement,
      rootHeight: 0,
      rootWidth: 0,
      ICONS_TOOLBAR_WIDTH,
      PANELS_WIDTH,
      POSITIONS_MANAGER_WIDTH,
      HEADER_HEIGHT,
      RIGHT_SECTIONS_HEIGHT_PADDING,
      PINTURA_MAX_MARGIN_LEFT,
      PANELS_HEIGHT_PADDING,
      PINTURA_MAX_MARGIN_RIGHT,
      activePanel: HOME_PANEL,
      isModalOpen: false,
      initializeCropEditor: false,
      pinturaPreviewImage: null as null | string,
      isPinturaExplicitDisableFlag: false,
      isConcept: false,
      showPreview: false,
      triggerSmallDetailsCheck: false,
      lastSelectedPosition: '',
      zoomLevelPintura: undefined as number | undefined,
      fitToViewZoomLevel: undefined as number | undefined,
      canvasSelection: { x: 0, y: 0, width: 0, height: 0 },
      initializeTextBox: false,
      myOptions: {
        labels: {
          buttonSkip: this.$t('main.wizard-tour-skip'),
          buttonPrevious: this.$t('section.actions.back'),
          buttonNext: this.$t('main.wizard-tour-next'),
          buttonStop: this.$t('wizard-v2.tour.modal.start-personalisation'),
        },
      },
      steps: [] as WizardTourStep[],
      activeInteractionMode: '',
      zoomLevelToolbar: 0,
      isLoadingActive: false,
      isAsAService: process.env.VUE_APP_AS_A_SERVICE === 'true',
    };
  },

  computed: {
    ...mapState(useGlobalStore, ['isLoaderActive']),
    ...mapState(useShapeStore, [
      'personalization',
      'activeShapeId',
      'shapes',
      'positionShapes',
    ]),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapStores(
      useProductStore,
      useShapeStore,
      useColorsManagementStore,
      useMirrorStore,
      useHistoryStore,
      useNotificationStore,
      useMirrorStore
    ),
    ...mapState(useColorsManagementStore, ['availablePmsColors']),
    ...mapState(useProductStore, [
      'activePosition',
      'area',
      'rotation',
      'unavailablePrintPositions',
      'selectedPrintPosition',
      'selectedVariant',
      'positionsSetFromProps',
      'data',
      'isProductPrintable',
      'editedPositions',
      'isLaserConverter',
      'isDebossingTechnique',
      'isSmallDetailsTechnique',
      'previousManagedPrintPositionId',
      'printPositionScaleFactor',
    ]),

    isEditMode(): boolean {
      return this.personalizationId !== '';
    },

    isPositionEmpty(): boolean {
      if (this.selectedPrintPosition && this.selectedPrintPosition.id) {
        return this.positionShapes(this.selectedPrintPosition.id).length === 0;
      }

      return true;
    },

    orderDetailsLeft() {
      let orderDetailsLeft = 0;

      const pinturaContainerWidth =
        this.rootWidth -
        ICONS_TOOLBAR_WIDTH -
        PANELS_WIDTH -
        POSITIONS_MANAGER_WIDTH -
        25;

      if (pinturaContainerWidth > ORDER_DETAILS_WIDTH) {
        orderDetailsLeft =
          (pinturaContainerWidth - ORDER_DETAILS_WIDTH) / 2 +
          ICONS_TOOLBAR_WIDTH +
          PANELS_WIDTH;
      } else {
        orderDetailsLeft = ICONS_TOOLBAR_WIDTH + PANELS_WIDTH;
      }

      if (this.context === 'CustomerCare') {
        orderDetailsLeft += 250;
      }
      return `${orderDetailsLeft}px`;
    },

    modalHandlers() {
      return {
        beforeClose: () => {
          this.isModalOpen = false;
        },
      };
    },

    pinturaContainer(): {
      width: number;
      height: number;
    } {
      const containerWidth =
        this.rootWidth -
        ICONS_TOOLBAR_WIDTH -
        PANELS_WIDTH -
        POSITIONS_MANAGER_WIDTH;

      const containerHeight =
        this.rootHeight -
        HEADER_HEIGHT -
        ORDER_DETAILS_HEIGHT -
        RIGHT_SECTIONS_HEIGHT_PADDING;

      return {
        width: containerWidth,
        height: containerHeight,
      };
    },

    activePanelLabel(): string {
      if (this.activePanel === IMAGE_PANEL) {
        return this.$t('wizard-v2.common.image').toString();
      } else if (this.activePanel === TEXT_PANEL) {
        return this.$t('wizard-v2.common.text').toString();
      } else if (this.activePanel === LANYARDS_PANEL) {
        return this.$t('wizard-v2.common.lanyard-options').toString();
      }
      return this.$t('wizard-v2.common.working-area').toString();
    },

    productName(): string {
      return this.data && this.data.name;
    },
  },

  watch: {
    async pdpChoices() {
      // TO DO : Cover redirect from Logo Only to wizard flow
      await this.initializeStateForRegularMode();

      this.initializeMirrorPositions();

      await this.getAvailablePmsColors();
    },
    selectedPrintPosition() {
      if (
        this.selectedPrintPosition.id &&
        this.lastSelectedPosition !== this.selectedPrintPosition.id
      ) {
        this.changeActivePanel(HOME_PANEL);
        this.lastSelectedPosition = this.selectedPrintPosition.id;
      }
    },
    async languageInUse() {
      await this.setDefaultTranslations();

      if (process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false') {
        var translationsJson = await customersAPI.getTranslations(
          this.languageInUse
        );
        this.$i18n.setLocaleMessage(this.languageInUse, translationsJson.data);
      }

      this.$i18n.locale = this.languageInUse;

      this.initializeWizardTourSteps();
    },
    async personalizationJson(newVal) {
      await this.usePersonalizationJson(newVal);
    },
  },

  async mounted() {
    this.$nextTick(() => {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');

      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.querySelector('.import-wizard-v2')
          : document.body.querySelector('.import-wizard-v2');

      if (element) {
        this.rootElement = element as HTMLElement;
        this.rootHeight = (element as HTMLElement).offsetHeight;
        this.rootWidth = (element as HTMLElement).offsetWidth;
      }

      window.addEventListener('resize', this.onResize);
    });
    if (this.context === 'CustomerCare') {
      if (this.personalizationJson && this.personalizationJson.form) {
        try {
          await this.usePersonalizationJson(this.personalizationJson);
        } catch (e: unknown) {
          console.error(e);
        }
      }
    } else {
      if (this.personalizationId !== '') {
        await this.initializeStateForEditMode();
      } else {
        await this.initializeStateForRegularMode();
      }
    }

    this.initializeMirrorPositions();

    await this.getAvailablePmsColors();

    const snapshot = createStateSnapshot();
    this.recordHistory(snapshot);

    if (this.isFirstTimeOpened) {
      this.openHelpModal();
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    ...mapActions(useShapeStore, [
      'getPersonalization',
      'initializeStateFromPersonalization',
      'initializeStateFromPersonalizationJson',
      'addTextPayload',
      'deselectShape',
      'selectShape',
      'addArtworkPayload',
      'setSmallDetailsCheckToDefault',
      'updateShapePosition',
      'setIsMoccaPersonalization',
      'resetShapeStateToDefault',
      'alignShape',
    ]),
    ...mapActions(useProductStore, [
      'getProduct',
      'setUnavailablePrintPositions',
      'setSelectedVariant',
      'setSelectedVariants',
      'setSelectedPrintPosition',
      'printPositionArea',
      'resetProductStateToDefault',
    ]),
    ...mapActions(useMirrorStore, [
      'initializeMirrorSettingsFromProof',
      'initializeMirrorSetting',
      'initializeMirrorPositions',
    ]),
    ...mapActions(useColorsManagementStore, [
      'getAvailablePmsColors',
      'resetColorsStateToDefault',
    ]),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    ...mapActions(useLangStore, ['setDefaultTranslations']),

    handleLoadingNotificationsAndChecks(loading: any) {
      if (typeof loading === 'string') {
        if (loading === 'start') {
          this.isLoadingActive = true;
        } else if (loading === 'end') {
          this.isLoadingActive = false;
        }
      }
    },

    async usePersonalizationJson(
      personalizationJson: CreatePersonalizationFormWrapper
    ) {
      this.resetColorsStateToDefault();
      this.resetShapeStateToDefault();
      this.resetProductStateToDefault();

      this.setIsMoccaPersonalization(true);

      const productSKU = personalizationJson.form.productSKU;
      let product: ProductPrintData | undefined;
      if (productSKU !== '') {
        product = await this.getProduct(productSKU);
      }

      const printPositionsData =
        personalizationJson.form.newProofForm.newProductPrintPositionForms;

      const printPostionAddModel: any[] = printPositionsData.map((pos: any) => {
        const position =
          product && product.productPrintPositions
            ? product.productPrintPositions.find((p) => p.name === pos.SAPCode)
            : undefined;

        const technique = position?.printTechniques.find(
          (t) => t.printTechnique.code === pos.productPrintTechnique.SAPCode
        );

        if (technique) {
          return {
            maxHeightMm: position?.maxHeightMm,
            maxWidthMm: position?.maxWidthMm,
            positionId: position?.id,
            positionName: pos.name,
            printPositionSAPCode: position?.name,
            technique: {
              id: technique.id,
              printTechniqueSAPCode: pos.productPrintTechnique.SAPCode,
              techniqueName: pos.productPrintTechnique.name,
              groupCode: technique.printTechnique.group.code,
            },
          } as ProductPrintPositionAddModel;
        }
      });

      this.setSelectedVariants(personalizationJson.form.productVariants);

      const variant = mapSelectedVariant(
        personalizationJson.form.productVariants[0].variantColor
      );

      if (variant) this.setSelectedVariant(variant);

      this.pdpChoices.productVariants = personalizationJson.form
        .productVariants as ProductColorSetterType[];

      this.setUnavailablePrintPositions(printPostionAddModel);

      if (printPostionAddModel) {
        this.setSelectedPrintPosition(printPostionAddModel[0].positionId);
      }

      await this.initializeStateFromPersonalizationJson(
        this.personalizationJson
      );

      this.runValidators();
    },

    setPanInteractionMode() {
      this.activeInteractionMode = 'pan';
    },

    setAutoInteractionMode() {
      this.activeInteractionMode = 'auto';
    },

    setZoomLevel(level: number) {
      this.zoomLevelPintura = level;
    },

    setFitToViewZoomLevel(level: number) {
      if (this.zoomLevelPintura === undefined) {
        this.zoomLevelPintura = level;
        this.zoomLevelToolbar = level;
        this.fitToViewZoomLevel = level;
      } else {
        this.zoomLevelToolbar = level;
      }
    },

    setRecommendedPrintPosition() {
      this.calculateShapePositionBasedOnDirection();
    },

    calculateShapePositionBasedOnDirection() {
      if (!this.activePosition.anchorPoint) {
        return;
      }

      const { anchorDirection } = this.activePosition.anchorPoint;

      const alignIfEnabled = (alignment: string) => {
        if (!isAlignButtonDisabled(alignment)) {
          this.alignShape(this.activeShapeId, alignment, true);
          this.runValidators();
        }
      };

      switch (anchorDirection) {
        case 'Up':
          alignIfEnabled('align-vertical-top');
          alignIfEnabled('align-horizontal-center');
          break;
        case 'Down':
          alignIfEnabled('align-vertical-bottom');
          alignIfEnabled('align-horizontal-center');
          break;
        case 'Left':
          alignIfEnabled('align-horizontal-left');
          alignIfEnabled('align-vertical-center');
          break;
        case 'Right':
          alignIfEnabled('align-horizontal-right');
          alignIfEnabled('align-vertical-center');
          break;
        default:
          return;
      }
    },

    initializeWizardTourSteps() {
      this.myOptions = {
        labels: {
          buttonSkip: this.$t('main.wizard-tour-skip'),
          buttonPrevious: this.$t('section.actions.back'),
          buttonNext: this.$t('main.wizard-tour-next'),
          buttonStop: this.$t('wizard-v2.tour.modal.start-personalisation'),
        },
      };

      // Use Vue.set or direct mutation to maintain reactivity
      this.$set(this, 'steps', tourStepsMapper());
    },

    openHelpModal() {
      this.$modal.show('tour-modal');
    },

    onAddLogoAction() {
      this.openLogoLibraryModal();
      this.activePanel = IMAGE_PANEL;
    },

    onAddTextAction() {
      this.activePanel = TEXT_PANEL;
      this.initializeTextBox = true;
    },

    isManualCheckVisible(): boolean {
      const usedTechniques = this.unavailablePrintPositions.map((x) => {
        return this.data.productPrintPositions
          .find((p) => p.name === x.positionName)
          ?.printTechniques.find(
            (t) => t.printTechnique.code === x.technique.printTechniqueSAPCode
          );
      });
      return usedTechniques.some((x) => x?.printTechnique.limitation !== null);
    },

    resetFlag() {
      this.triggerSmallDetailsCheck = false;
      this.$emit('action:trigger-small-details');
    },

    onResize() {
      this.$nextTick(() => {
        if (
          this.rootHeight > this.rootElement.offsetHeight &&
          this.rootHeight > this.rootElement.scrollHeight
        ) {
          this.rootHeight = this.rootElement.offsetHeight;
        } else {
          this.rootHeight =
            this.rootElement.scrollHeight > this.rootElement.offsetHeight
              ? this.rootElement.scrollHeight
              : this.rootElement.offsetHeight;
        }

        this.rootWidth = this.rootElement.offsetWidth;
      });
    },

    changeActivePanel(panel: string) {
      this.deselectShape();
      this.activePanel = panel;
      this.initializeTextBox = false;
    },

    async initializeStateForRegularMode() {
      if (this.pdpChoices.masterProductSKU !== '') {
        await this.getProduct(this.pdpChoices.masterProductSKU);

        const mappedPositions = mapPositionsFromPdp(this.pdpChoices);
        this.setUnavailablePrintPositions(mappedPositions);

        if (mappedPositions && mappedPositions.length > 0) {
          this.setSelectedPrintPosition(mappedPositions[0].positionId);
        }

        this.setSelectedVariants(this.pdpChoices.productVariants);

        const variant = mapSelectedVariant(
          this.pdpChoices.productVariants[0].variantColor
        );

        if (variant) this.setSelectedVariant(variant);

        // if the user is redirected from logo only flow
        // logoOnlyArtwork prop will have a length greater than 0
        // since Intershop will send the logo only artwork as a component prop
        if (this.logoOnlyArtwork.length > 0) {
          await this.initializeStateAfterLogoOnlyRedirection();
        }

        this.initializeMirrorSetting();

        if (mappedPositions.length > 0) {
          this.runValidators();
        }
      }
    },

    async initializeStateForEditMode() {
      await this.getPersonalization(this.personalizationId);

      if (this.personalization) {
        await this.getProduct(this.personalization.productSKU);

        const mappedPositions = mapProductPrintPositions();

        this.setUnavailablePrintPositions(mappedPositions);

        if (mappedPositions) {
          this.setSelectedPrintPosition(mappedPositions[0].positionId);
        }

        const variant = mapSelectedVariant(
          this.personalization.productVariants[0].variantColor
        );
        if (variant) {
          this.setSelectedVariant(variant);
          this.setSelectedVariants(this.personalization.productVariants);
        }

        await this.initializeStateFromPersonalization();

        this.initializeMirrorSettingsFromProof(this.personalization.proofs[0]);

        this.runValidators();
      }
    },

    async initializeStateAfterLogoOnlyRedirection() {
      for (let i = 0; i < this.pdpChoices.productPrintPositions.length; i++) {
        const position = this.pdpChoices.productPrintPositions[i];
        const currentPosition = this.productV2Store.printPositions.find(
          (pos) => pos.name === position.printPositionSAPCode
        );

        const currentTechnique = currentPosition?.printTechniques.find(
          (tech) => tech.printTechnique.code === position.printTechniqueSAPCode
        );

        const blob = base64StringToBlob(
          this.logoOnlyArtwork[i].pngFile,
          'image/png'
        );

        const traditionalTechniquePngFile = this.logoOnlyArtwork[i]
          .traditionalTechniquePngFile
          ? base64StringToBlob(
              this.logoOnlyArtwork[i].traditionalTechniquePngFile,
              'image/png'
            )
          : '';

        if (currentPosition && currentTechnique) {
          await this.addArtworkPayload(
            {
              ...this.logoOnlyArtwork[i],
              pngFile: blob,
              pngFileWithoutConverters: blob,
              processed: '',
              customerId: '',
              thumbnailUrl: '',
              variantArtworkImages: [],
              traditionalTechniquePngFile: traditionalTechniquePngFile,
            } as ArtworkPayload,
            currentPosition.id
          );

          const limitation = currentTechnique.printTechnique.limitation;
          if (limitation) {
            this.setSmallDetailsCheckToDefault(this.shapes[i].id);
          }
        }
      }

      const eventPayload = generateEventPayloadV2();

      this.setActiveShapeId(this.logoOnlyArtwork[0].id);

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
    },

    async addToImageDecoration(shape: AnnotationShape) {
      if (shape.kind === 'Text') {
        await this.addTextPayload({ ...shape, kind: 'Text' });

        if (shape.w2pText.text !== '') {
          const snapshot = createStateSnapshot();
          this.recordHistory(snapshot);
        }
      }
    },

    openLogoLibraryModal() {
      /**
       * Disable pintura while the logo library modal is open
       */
      this.isModalOpen = true;

      /**
       * Open logo library modal
       */
      this.$modal.show('logo-library-modal', { foo: 'bar' });
    },
    openCropEditor() {
      this.initializeCropEditor = true;
    },
    setActiveShapeId(id: string) {
      this.selectShape(id);
    },
    async onImportArtwork(payload: ArtworkPayload) {
      this.setIsLoaderActive(true);
      await this.addArtworkPayload(
        payload,
        this.productV2Store.selectedPrintPosition.id
      );
      this.setActiveShapeId(payload.id);

      if (
        this.mirrorV2Store.currentMirrorSettings &&
        this.mirrorV2Store.currentMirrorSettings.isMirrorApplied
      ) {
        this.mirrorV2Store.addMirrorShape(
          this.shapeV2Store.shapeById(
            this.shapeV2Store.activeShapeId
          ) as AnnotationShape
        );
      }

      this.setIsLoaderActive(false);
      const eventPayload = generateEventPayloadV2();
      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
      this.setRecommendedPrintPosition();
      this.runValidators();
      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      this.$modal.hide('logo-library-modal');
      this.$modal.hide('small-details-single-logo-modal');
    },
    beforeSelectShape(target: AnnotationShape) {
      if (target.kind === 'Text') {
        this.activePanel = TEXT_PANEL;
      } else if (target.kind === 'Logo') {
        this.activePanel = IMAGE_PANEL;
      }
      this.selectShape(target.id);
    },
    startTour() {
      this.$modal.hide('tour-modal');
      var importWizard = document.querySelector('w2p-import-wizard-wrapper');
      var importWizardOld = document.querySelector('.import-wizard-v2');
      if (importWizard?.shadowRoot) {
        var tourElement = importWizard?.shadowRoot.querySelector('#wizardTour');
        if (tourElement) {
          (tourElement as any).__vue__.$tours['wizardTour'].start();
        }
      } else if (importWizardOld?.shadowRoot) {
        var tourElementOld =
          importWizardOld?.shadowRoot.querySelector('#wizardTour');
        if (tourElementOld) {
          (tourElementOld as any).__vue__.$tours['wizardTour'].start();
        }
      } else {
        (this as any).$tours['wizardTour'].start();
      }
    },
    triggerSmallDetails(isConcept: boolean) {
      this.triggerSmallDetailsCheck = true;
      this.isConcept = isConcept;
    },
    showRecommendedPrintPositionModal(showModal: boolean) {
      if (showModal) {
        this.$modal.show('recommended-print-position-modal');
      } else {
        this.$modal.hide('recommended-print-position-modal');
      }
    },
    async constructPersonalizationJSON() {
      const { sku, name } = this.data;
      const files = getArtworkFiles(this.shapes);
      const notificationStore = useNotificationStore();

      try {
        const form: CreatePersonalizationForm = {
          externalCustomerId: this.$sapCustomerId,
          productSKU: sku,
          productName: name,
          isConcept: this.isConcept,
          isDirectApproval: this.$isDirectApproval,
          customerCompanyLogoUrl: this.$customerCompanyLogoUrl,
          contactLanguageCode: this.languageInUse,
          creator: '',
          proofType: 'AdvancedPersonalization',
          productVariants: this.personalizationJson.form.productVariants,
          newProofForm: getProofFormFromState(false),
          remarkCodes: extractCommercialRemarks(
            notificationStore.notifications
          ),
        } as unknown as CreatePersonalizationForm;

        return {
          form: form,
          files: files,
        } as ConstructPersonalizationFormWrapper;
      } catch (e: unknown) {
        console.log((e as Error).message);
        return null;
      }
    },
  },
});
